import { Capacitor } from '@capacitor/core';
import { FEATURE_FLAGS_STORAGE_KEY } from '../constants/storageKeys';

/**
 * Where the API lives — decided once, at module load, for the whole app.
 *
 * In a browser the page is served from the workspace's own host
 * (`<slug>.aquaerp.in`), so the backend is either the build-time
 * REACT_APP_BACKEND_URL or, when that is empty (Docker, production), the same
 * origin via nginx. Nothing about that path changes here.
 *
 * In the store app the page is served from the phone itself
 * (`https://localhost` on Android, `capacitor://localhost` on iOS), so "same
 * origin" means nothing and the workspace has to be asked for once. It is kept
 * as an origin (`https://<slug>.aquaerp.in`) and every request goes there —
 * which is also what makes the tenant resolve: the backend reads the tenant
 * from the Host header first, exactly as it does for the browser.
 *
 * Switching workspace reloads the app rather than re-pointing a live one, so
 * every module that captured the base at import time captures the new one.
 */

export const IS_NATIVE = Capacitor.isNativePlatform();
export const NATIVE_PLATFORM = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
export const ROOT_DOMAIN = process.env.REACT_APP_ROOT_DOMAIN || 'aquaerp.in';

const WORKSPACE_KEY = 'erp_workspace_origin_v1';

function readStoredOrigin() {
  try {
    return localStorage.getItem(WORKSPACE_KEY) || '';
  } catch {
    return '';
  }
}

function normalizeEnvBackend() {
  const url = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
  if (!url) return '';
  const lower = url.toLowerCase();
  if (lower.includes('yourdomain.com') || lower.includes('example.com') || lower.includes('placeholder')) {
    return '';
  }
  return url;
}

/** The chosen workspace's origin in the app; '' in a browser or before one is chosen. */
export const WORKSPACE_ORIGIN = IS_NATIVE ? readStoredOrigin() : '';

/** Backend origin with no trailing slash; '' means same-origin `/api`. */
export const BACKEND_URL = IS_NATIVE ? WORKSPACE_ORIGIN : normalizeEnvBackend();

/** `${BACKEND_URL}/api`, or the relative `/api` the nginx / dev proxy serves. */
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

/** Host the app is talking to, for display ("varma.aquaerp.in"). */
export function workspaceLabel() {
  if (!WORKSPACE_ORIGIN) return '';
  try {
    return new URL(WORKSPACE_ORIGIN).host;
  } catch {
    return WORKSPACE_ORIGIN;
  }
}

/**
 * What the operator typed → an origin. Accepts the short company code
 * ("varma" → https://varma.aquaerp.in), a full host for a tenant on its own
 * domain ("erp.example.co.in"), or — for a developer pointing a test build at
 * a laptop — a URL with its scheme ("http://192.168.1.20:3000").
 */
export function workspaceOriginFor(input) {
  const raw = (input || '').trim().toLowerCase().replace(/\/+$/, '');
  if (!raw) return '';
  if (/^https?:\/\//.test(raw)) return raw;
  if (raw.includes('.')) return `https://${raw.replace(/[^a-z0-9.-]/g, '')}`;
  const slug = raw.replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  return slug ? `https://${slug}.${ROOT_DOMAIN}` : '';
}

/** True when the operator typed a developer URL — those skip the workspace lookup. */
export function isExplicitOrigin(input) {
  return /^https?:\/\//i.test((input || '').trim());
}

export function saveWorkspaceOrigin(origin) {
  try {
    localStorage.setItem(WORKSPACE_KEY, origin);
  } catch {
    /* nothing to do — the picker will simply be shown again */
  }
}

/**
 * Leave the workspace: sign out of it and forget it, then reload so every
 * module re-reads the base. The session belongs to that tenant, so it goes too.
 */
export function switchWorkspace() {
  try {
    ['token', 'user', 'isImpersonation', 'tenant_id_hint', FEATURE_FLAGS_STORAGE_KEY, WORKSPACE_KEY].forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
  window.location.replace('/');
}

/**
 * A server-relative file path (`/uploads/…`, `/api/…`) as something an <img>
 * or a link can load. In a browser the path is already same-origin and comes
 * back untouched; in the app it would resolve against the phone, so it is
 * prefixed with the workspace. Absolute URLs and blob:/data: pass through.
 */
export function assetUrl(path) {
  if (!path || typeof path !== 'string') return path;
  if (BACKEND_URL && path.startsWith('/') && !path.startsWith('//')) return `${BACKEND_URL}${path}`;
  return path;
}
